import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  fetchTasksMe,
  RM_TASKS_ENABLED,
  Task,
  tasksApi,
} from '../../../Tasks/tasksApi';

/** How often to check for newly-assigned tasks and due follow-ups. */
const POLL_MS = 25_000;

/**
 * Follow-ups already announced, as `taskId:followUpAt`. Kept in localStorage
 * so a page reload doesn't re-announce everything that is already overdue —
 * an RM refreshing their queue shouldn't set off five chimes.
 *
 * Keyed by the time as well as the task, so rescheduling a follow-up to a new
 * time announces again, which is the point of rescheduling it.
 */
const ALERTED_KEY = 'rm-tasks:followups-alerted';

const loadAlerted = (): Set<string> => {
  try {
    const raw = localStorage.getItem(ALERTED_KEY);
    return new Set<string>(raw ? (JSON.parse(raw) as string[]) : []);
  } catch {
    return new Set<string>();
  }
};

const saveAlerted = (keys: Set<string>): void => {
  try {
    // Keep the tail only — this grows one entry per follow-up, forever.
    // Array.from, not a spread: the build target predates Set iteration.
    localStorage.setItem(ALERTED_KEY, JSON.stringify(Array.from(keys).slice(-500)));
  } catch {
    /* private window or full quota — re-announcing is better than crashing */
  }
};

/**
 * Silent, app-wide watcher (renders nothing). While an agent is signed in and
 * has the CMS open, it polls their open tasks every ~25s and announces two
 * different things with a chime + toast + (if permitted) a desktop banner:
 *
 *   - a task newly assigned to them — detected by an id it hasn't seen;
 *   - a follow-up they set that has come due — which is the *same* task
 *     arriving back in their day, so it can't be detected by id at all. That
 *     distinction is why follow-ups went unannounced until now.
 *
 * Both the toast and the desktop banner are clickable and land on the work
 * itself: one task opens that ticket, several open the queue (follow-ups with
 * the Follow-ups chip already applied). Renewals live in that queue too, so
 * there is no longer a wrong list to land in.
 *
 * In-app only by design — no service worker / web push. Nothing fires while
 * the CMS is closed, so a follow-up due overnight announces when the RM next
 * opens the tool rather than at the moment it falls due. Browsers block audio
 * until the user interacts with the page, so we unlock an AudioContext on the
 * first gesture and synthesize the chime from it (no audio file needed).
 */
const TaskNotifier = () => {
  const seen = useRef<Set<string>>(new Set());
  const baselined = useRef(false); // first poll seeds `seen` without alerting
  const audioCtx = useRef<AudioContext | null>(null);
  const meId = useRef<string | null>(null);
  /** Follow-ups already announced — survives a reload, unlike `seen`. */
  const alerted = useRef<Set<string>>(loadAlerted());
  /**
   * Routing is held in a ref because the polling effect is mounted once, for
   * the session, and must not be torn down and restarted on navigation.
   */
  const navigate = useNavigate();
  const navRef = useRef(navigate);
  navRef.current = navigate;

  // Unlock audio + ask for desktop-notification permission on the first gesture.
  useEffect(() => {
    if (!RM_TASKS_ENABLED) return;
    const unlock = () => {
      if (!audioCtx.current) {
        const Ctor: typeof AudioContext | undefined =
          window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
        if (Ctor) audioCtx.current = new Ctor();
      }
      audioCtx.current?.resume().catch(() => {});
      if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
        Notification.requestPermission().catch(() => {});
      }
      window.removeEventListener('pointerdown', unlock);
      window.removeEventListener('keydown', unlock);
    };
    window.addEventListener('pointerdown', unlock);
    window.addEventListener('keydown', unlock);
    return () => {
      window.removeEventListener('pointerdown', unlock);
      window.removeEventListener('keydown', unlock);
    };
  }, []);

  useEffect(() => {
    if (!RM_TASKS_ENABLED) return;
    let stopped = false;
    let timer: ReturnType<typeof setTimeout>;

    const poll = async () => {
      try {
        if (meId.current === null) {
          const me = await fetchTasksMe();
          if (!me) return; // not a provisioned agent — nothing to watch
          meId.current = me.externalId;
        }
        const { tasks } = await tasksApi<{ tasks: Task[]; total: number }>(
          `/v1/tasks?assigneeExternalId=${encodeURIComponent(meId.current)}&open=true&take=200`,
        );
        const fresh: Task[] = [];
        /**
         * Follow-ups whose time has come. Unlike a new task this is the *same*
         * ticket arriving back in the RM's day, so it can't be detected by id
         * — it's the clock that changed, not the queue.
         */
        const due: Task[] = [];
        const now = Date.now();
        for (const t of tasks) {
          if (!seen.current.has(t.id)) {
            if (baselined.current) fresh.push(t);
            seen.current.add(t.id);
          }
          if (t.followUpAt && new Date(t.followUpAt).getTime() <= now) {
            const key = `${t.id}:${t.followUpAt}`;
            if (!alerted.current.has(key)) {
              alerted.current.add(key);
              due.push(t);
            }
          }
        }
        baselined.current = true;
        // A brand-new task that is somehow already due announces once, as a
        // new task — two alerts for one ticket would just be noise.
        const dueOnly = due.filter((t) => !fresh.some((f) => f.id === t.id));
        if (fresh.length > 0) notify(fresh);
        if (dueOnly.length > 0) {
          notifyFollowUps(dueOnly);
          saveAlerted(alerted.current);
        }
      } catch {
        /* transient failure — try again next tick */
      } finally {
        if (!stopped) timer = setTimeout(poll, POLL_MS);
      }
    };

    /**
     * Where an alert takes you when clicked. One task opens that ticket;
     * several open the list, filtered where there's a sensible filter — the
     * point is to land on the work, not on a page you then have to search.
     */
    const go = (path: string) => {
      navRef.current(`${process.env.PUBLIC_URL}${path}`);
    };

    /** Same destination for the toast and the desktop banner. */
    const clickable = (path: string) => ({
      onClick: () => go(path),
      style: { cursor: 'pointer' },
    });

    const banner = (title: string, body: string, path: string) => {
      if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
      try {
        const n = new Notification(title, { body });
        n.onclick = () => {
          // The browser may be in the background — bring it forward, or the
          // navigation happens somewhere nobody is looking.
          window.focus();
          n.close();
          go(path);
        };
      } catch {
        /* Notification constructor can throw on some platforms */
      }
    };

    const notify = (fresh: Task[]) => {
      playChime();
      const msg =
        fresh.length === 1
          ? `New ${fresh[0].taskType.name} · ${fresh[0].customerName}`
          : `${fresh.length} new tasks assigned to you`;
      const path = fresh.length === 1 ? `/pages/rm-tasks/${fresh[0].id}` : '/pages/rm-tasks';
      toast.info(msg, { autoClose: 8000, ...clickable(path) });
      // Tell any open task list / dashboard to reload so the new task appears
      // without a manual refresh.
      window.dispatchEvent(new CustomEvent('rm-tasks:changed'));
      banner('New task assigned', msg, path);
    };

    /**
     * A follow-up the RM set has come due. Worded differently from a new task
     * on purpose: this is something they promised the learner, not work that
     * has just landed, and the learner's name is the useful part.
     */
    const notifyFollowUps = (dueTasks: Task[]) => {
      playChime();
      const msg =
        dueTasks.length === 1
          ? `Follow-up due · ${dueTasks[0].customerName}${
              dueTasks[0].followUpNote ? ` — ${dueTasks[0].followUpNote}` : ''
            }`
          : `${dueTasks.length} follow-ups are due`;
      // A single one opens the ticket; several open the queue already on the
      // Follow-ups chip, so the list is exactly the ones being announced.
      const path =
        dueTasks.length === 1
          ? `/pages/rm-tasks/${dueTasks[0].id}`
          : '/pages/rm-tasks?urgency=FOLLOWUP';
      // Longer than a new-task toast: a follow-up is a commitment with a time
      // on it, and it's easy to miss one while reading something else.
      toast.warn(msg, { autoClose: 15000, ...clickable(path) });
      window.dispatchEvent(new CustomEvent('rm-tasks:changed'));
      banner(
        dueTasks.length === 1 ? 'Follow-up due' : `${dueTasks.length} follow-ups due`,
        msg,
        path,
      );
    };

    const playChime = () => {
      const ctx = audioCtx.current;
      if (!ctx) return; // no user gesture yet → can't play; toast still shows
      const now = ctx.currentTime;
      [880, 1174.66].forEach((freq, i) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.value = freq;
        const t = now + i * 0.14;
        gain.gain.setValueAtTime(0, t);
        gain.gain.linearRampToValueAtTime(0.18, t + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.3);
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start(t);
        osc.stop(t + 0.32);
      });
    };

    poll();
    return () => {
      stopped = true;
      clearTimeout(timer);
    };
  }, []);

  return null;
};

export default TaskNotifier;
